import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lessonsApi, type StreamForStreamDashboard } from 'Lessons';
import { isNotLoggedInError } from 'BackRoyalApi';
import { useStreamRequestOptions } from './useStreamRequestOptions';

const { useGetStreamForDashboardQuery } = lessonsApi;

/*
    Loads the stream behind the dashboard over HTTP. Use this only when the app is online;
    useStoredStreamForDashboard covers offline mode, and StreamDashboardComponent picks between them.

    Progress arrives inside this payload, under lessonStreamsProgress on the stream and
    lessonProgress on each lesson, so there is no join to do here and no need for
    useContentProgress. That also means this hook serves signed-out visitors, who simply get a
    payload with no progress in it.
*/
export function useStreamForDashboard(streamId: string): {
    stream: StreamForStreamDashboard | null;
    isLoading: boolean;
    hasError: boolean;
    retry: () => void;
} {
    const navigate = useNavigate();
    const { userCohortIds, prefLocale } = useStreamRequestOptions();

    const { data, isLoading, error, refetch } = useGetStreamForDashboardQuery({
        id: streamId,
        userCohortIds,
        prefLocale,
    });

    /*
        RTK Query skips ApiErrorHandler, so redirect to sign-in with target here. Only a 401 with
        not_logged_in redirects. Any other 401 is a permission error and would loop.
    */
    const shouldRedirectToSignIn = isNotLoggedInError(error);
    useEffect(() => {
        if (!shouldRedirectToSignIn) return;
        const target = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        navigate(`/sign-in?target=${encodeURIComponent(target)}`, { replace: true });
    }, [shouldRedirectToSignIn, navigate]);

    return {
        stream: data?.contents.lessonStreams[0] ?? null,
        isLoading,

        // Anything the redirect does not handle is a real failure. Without this the page has no
        // stream and no error, and StreamDashboardComponent spins for ever.
        hasError: !!error && !shouldRedirectToSignIn,
        retry: refetch,
    };
}
